/* Right-click a column header: set that tech's hours for the day, bypass them
 * in the turn queue, or take them off the board altogether. Anchored at the
 * pointer rather than centred, because it acts on the column you clicked.
 *
 * The hours come first, and they are the only part of this menu that is not
 * about right now. A bypass is a thing happening at the moment somebody opens
 * this — she has gone on a break, she is not coming back — and the hours are
 * the opposite: they are set once in the morning and their whole point is that
 * nobody has to remember them at half four. See js/rules/shift.js.
 *
 * Skipping asks WHY rather than being a plain on/off. The reason is the answer
 * to the question the desk actually gets asked twenty minutes later — is she
 * coming back? — and it is what the column badge shows.
 *
 * Taking somebody off is the other half of the + at the end of the headers, and
 * it lives here rather than in Settings for the same reason the + does: who is
 * in today is a fact about today, and the day is this screen. */
import * as Vue from 'vue';
import { store } from '../store.js';
import { useMenuFocus } from '../menu-focus.js';

var MENU_W = 296;
var MENU_H = 500;   // enough to keep the whole card on screen near an edge
var EDGE = 8;

export const StaffMenu = {
  name: 'StaffMenu',
  setup: function () {
    var staff = Vue.computed(function () {
      if (!store.ui.staffMenu) return null;
      return store.staffOf(store.ui.staffMenu.staffId);
    });

    // Clamped so a right-click on the last column does not open off-screen.
    var pos = Vue.computed(function () {
      var m = store.ui.staffMenu;
      if (!m) return { left: '0px', top: '0px' };
      var w = window.innerWidth, h = window.innerHeight;
      return {
        left: Math.max(EDGE, Math.min(m.x, w - MENU_W - EDGE)) + 'px',
        top: Math.max(EDGE, Math.min(m.y, h - MENU_H - EDGE)) + 'px'
      };
    });
    var focus = useMenuFocus(Vue.computed(function () { return !!staff.value; }));

    var skipped = Vue.computed(function () {
      return !!(staff.value && store.isSkipped(staff.value.id));
    });

    var reason = Vue.computed(function () {
      return staff.value ? store.skipReasonOf(staff.value.id) : null;
    });

    // Only taking clients who ask for them — today's answer, which is the
    // roster's default unless the desk has said otherwise.
    var ownClients = Vue.computed(function () {
      return !!(staff.value && store.isOwnClients(staff.value.id));
    });
    var ownUsual = Vue.computed(function () {
      return !!(staff.value && staff.value.ownClientsOnly);
    });

    function toggleOwn() {
      store.setOwnClients(staff.value.id, !ownClients.value);
      store.closeStaffMenu();
    }

    var skipCount = Vue.computed(function () {
      return staff.value ? store.skipsFor(staff.value.id).length : 0;
    });

    // Picking the reason they are already out for puts them back, so the same
    // click that took them out returns them.
    function pick(id) {
      var current = reason.value;
      store.setSkipped(staff.value.id, current && current.id === id ? null : id);
      store.closeStaffMenu();
    }

    function back() {
      store.setSkipped(staff.value.id, null);
      store.closeStaffMenu();
    }

    // Off the board for the day. Nothing is deleted — the turns stay on the
    // day and come back with the column — so this asks no confirmation; the +
    // at the end of the headers is the way back, and it tags them "Back".
    function takeOff() {
      store.setPresent(staff.value.id, false);
      store.closeStaffMenu();
    }

    // Marking somebody out while a service is running would strand the square,
    // so the store refuses it. Saying so on the item beats a toast explaining
    // why the click did nothing.
    var busy = Vue.computed(function () {
      return !!(staff.value && store.isBusy(staff.value.id));
    });

    /* ---- their hours for the day ----
     *
     * The two fields read the same whether the times were typed or worked out,
     * which is deliberate: the desk is being shown what the board believes,
     * not a form with the interesting half left blank. What separates them is
     * the line underneath — either what the assumption was made from, or the
     * way back to it.
     */
    var shift = Vue.computed(function () {
      return staff.value ? store.shiftOf(staff.value.id) : null;
    });

    // `change` rather than `input`, so a write does not go up once per digit
    // as the field is filled in. Same reason as the business-hours rows.
    function setIn(evt) { store.setShiftTimes(staff.value.id, { in: evt.target.value }); }
    function setOut(evt) { store.setShiftTimes(staff.value.id, { out: evt.target.value }); }
    function resetShift() { store.clearShiftTimes(staff.value.id); }

    // Whether the hours have already stopped the column, which is the one
    // thing about them that is not visible from the two fields.
    var spent = Vue.computed(function () {
      return !!(staff.value && store.isShiftSpent(staff.value.id));
    });

    return {
      staff: staff, pos: pos, skipped: skipped, reason: reason, skipCount: skipCount,
      reasons: store.skipReasons,
      busy: busy,
      ownClients: ownClients,
      ownUsual: ownUsual,
      toggleOwn: toggleOwn,
      shift: shift,
      spent: spent,
      setIn: setIn,
      setOut: setOut,
      resetShift: resetShift,
      formatClock: store.formatClock,
      outGrace: store.shiftOutGraceMinutes,
      hoursSet: store.hoursSet,
      missed: Vue.computed(function () {
        return staff.value ? store.missedTurns(staff.value.id) : 0;
      }),
      turnCredit: store.turnCredit,
      pluralTurns: store.pluralTurns,
      pick: pick,
      back: back,
      takeOff: takeOff,
      menuEl: focus.menuEl,
      onMenuKey: focus.onMenuKey,
      close: store.closeStaffMenu
    };
  },
  template: [
    '<div v-if="staff" class="ctx-scrim" @click.self="close" @contextmenu.prevent="close">',
    '  <div class="ctx" :style="pos" role="menu"',
    '       ref="menuEl" @keydown="onMenuKey">',
    '    <div class="ctx__head">',
    '      <strong>{{ staff.name }}</strong>',
    /* The whole of this technician's day in one line, because this menu is now
       the only place it is written out: credit is what decides their place in
       the queue, and the two things making it up — turns actually taken, turns
       charged for being late or being out — are what somebody querying the
       number wants to see beside it. */
    '      <span>{{ pluralTurns(turnCredit(staff.id)) }}',
    '        <template v-if="missed"> &middot; {{ missed }} missed</template>',
    '        <template v-if="skipCount"> &middot; {{ skipCount }} skipped</template>',
    '        <template v-if="busy"> &middot; in service</template></span>',
    '    </div>',
    '',
    /* ---- the day's hours ----
     *
     * `<input type="time">` rather than a text box, the same as the business
     * hours in Settings: it hands back "HH:MM" or nothing at all, which is the
     * whole of the validation the store would otherwise be doing on
     * half-typed strings. An emptied field is not an error, it is "go back to
     * what the board assumes". */
    '    <p class="ctx__label">Hours today</p>',
    '    <div v-if="shift" class="ctx__hours">',
    '      <label class="ctx__hours-field">',
    '        <span class="ctx__hours-key">In</span>',
    '        <input class="input input--clock" type="time" :value="shift.startClock"',
    '          :aria-label="staff.name + \' start time today\'" @change="setIn" />',
    '      </label>',
    '      <span class="ctx__hours-dash">&ndash;</span>',
    '      <label class="ctx__hours-field">',
    '        <span class="ctx__hours-key">Out</span>',
    '        <input class="input input--clock" type="time" :value="shift.endClock"',
    '          :aria-label="staff.name + \' finish time today\'" @change="setOut" />',
    '      </label>',
    '    </div>',
    /* One line under the fields, and which line it is says everything the two
       boxes cannot: an early finish is a rule the board is now enforcing, and
       anything else is an assumption worth being able to see the workings of. */
    '    <p v-if="shift && shift.early" class="ctx__hint ctx__hint--note">',
    '      <template v-if="spent">No room left before {{ formatClock(shift.early) }}, so the rest of the column is blocked. Move the finish later to bring it back.</template>',
    '      <template v-else>Nothing that would run past {{ formatClock(shift.early) }} is offered to {{ staff.name }}.</template>',
    '    </p>',
    /* What the board will do on its own, said next to the field that arms it.
       Only for a typed Out time, because that is the only one it acts on, and
       phrased as a thing about to happen rather than a setting: there is
       nothing here to switch off, and the way back is the two buttons below. */
    '    <p v-if="shift && shift.customEnd && !skipped" class="ctx__hint">',
    '      At <strong>{{ formatClock(shift.endAt) }}</strong>, after their current service, the board marks them <strong>Done for today</strong>.</p>',
    '    <p v-else-if="shift" class="ctx__hint">',
    '      <template v-if="hoursSet">Assumes {{ outGrace }} min past closing. An earlier <strong>Out</strong> stops longer services being offered.</template>',
    '      <template v-else>Set <strong>Business hours</strong> in Settings to fill these in.</template>',
    '    </p>',
    '    <p v-if="shift && (shift.customStart || shift.customEnd)" class="ctx__act">',
    '      <button type="button" class="link-btn" @click="resetShift">Back to the usual hours</button></p>',
    '',
    '    <p class="ctx__label">{{ skipped ? "Skipping because" : "Skip / bypass" }}</p>',
    '    <button v-for="r in reasons" :key="r.id" type="button" class="ctx__item"',
    '      :class="{ \'is-on\': reason && reason.id === r.id }" role="menuitemradio"',
    '      :aria-checked="reason && reason.id === r.id ? \'true\' : \'false\'"',
    '      @click="pick(r.id)">',
    '      <span class="ctx__box" aria-hidden="true">{{ reason && reason.id === r.id ? "\\u2713" : "" }}</span>',
    '      <span class="ctx__label-text">{{ r.label }}</span>',
    '    </button>',
    '',
    '    <button v-if="skipped" type="button" class="ctx__item ctx__item--back" @click="back">',
    '      <span class="ctx__box" aria-hidden="true"></span>',
    '      <span class="ctx__label-text">Back in the rotation</span>',
    '    </button>',
    '',
    '    <p class="ctx__hint">',
    '      <template v-if="skipped">Turns are passing {{ staff.name }} by.</template>',
    '      <template v-else>Stays on the board, out of the queue.</template>',
    '    </p>',
    '    <p v-if="!skipped && busy" class="ctx__hint ctx__hint--note">',
    '      Their current service keeps running.</p>',
    '',
    /* The other half of the + at the end of the headers. Kept well clear of the
       three reasons above it: skipping leaves the column standing as the day's
       record, and this takes it away, which is a different answer to a
       different question — "she is on a break" against "she is not in". */
    /* Who they take, rather than whether they take anything. Its own section
       and not a fourth skip reason: somebody on their own clients is working,
       and a client who asks for them still goes straight to them. */
    '    <p class="ctx__label">Clients today</p>',
    '    <button type="button" class="ctx__item" :class="{ \'is-on\': ownClients }"',
    '      role="menuitemcheckbox" :aria-checked="ownClients ? \'true\' : \'false\'" @click="toggleOwn">',
    '      <span class="ctx__box" aria-hidden="true">{{ ownClients ? "\\u2713" : "" }}</span>',
    '      <span class="ctx__label-text">Own clients only</span>',
    '    </button>',
    '    <p class="ctx__hint">',
    '      <template v-if="ownClients">Only clients who ask for them &mdash; no rotation, stand-ins or skips.</template>',
    '      <template v-else>Out of the rotation; requests still go to them.</template>',
    '      <template v-if="ownClients !== ownUsual"> Today only &mdash; usually {{ ownUsual ? "on" : "off" }}.</template>',
    '    </p>',
    '',
    '    <p class="ctx__label">Not in today</p>',
    '    <button type="button" class="ctx__item ctx__item--out" :disabled="busy"',
    '      :title="busy ? \'Stop their service first\' : \'\'" @click="takeOff">',
    '      <span class="ctx__box" aria-hidden="true">&times;</span>',
    '      <span class="ctx__label-text">Take off the board</span>',
    '    </button>',
    '    <p class="ctx__hint">',
    '      <template v-if="busy">Can&rsquo;t come off while a service is running.</template>',
    '      <template v-else>Nothing is deleted &mdash; the <strong>+</strong> past the last column brings them back.</template>',
    '    </p>',
    '  </div>',
    '</div>'
  ].join('\n')
};
